import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Space,
  Tag,
  Switch,
  Select,
  message,
  Typography,
  Modal,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  ApartmentOutlined,
  NotificationOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import ProjectModal from "./ProjectModal";
import PartnersManager from "./PartnersManager";
import NewsAdmin from "./NewsAdmin";
import TableWithExport from "../../../generic components/TableWithExport/TableWithExport";

const { Text } = Typography;

const statusTag = (v) =>
  v === true ? (
    <Tag color="green">Published</Tag>
  ) : v === false ? (
    <Tag color="red">Declined</Tag>
  ) : (
    <Tag>Pending</Tag>
  );

const orderKey = (o) =>
  o == null || Number(o) <= 0 ? Number.POSITIVE_INFINITY : Number(o);

export default function CapacityProjectsAdmin() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all"); // all|pending|published|declined

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [partnersOpen, setPartnersOpen] = useState(false);
  const [partnersProject, setPartnersProject] = useState(null);

  const [newsOpen, setNewsOpen] = useState(false);
  const [newsProject, setNewsProject] = useState(null);

  const deleteOne = async (record) => {
    try {
      await axios.delete(routes.capacityAdminItem(record._id));
      message.success("Project deleted");
      fetchList();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete project");
    }
  };

  const deleteMany = async (selected) => {
    const ids = selected.map((r) => r._id);
    try {
      // Fire deletes sequentially to be gentle on the API; adjust to Promise.all if preferred
      for (const id of ids) {
        // capacityAdminItem is RESTful; assume DELETE removes the project
        await axios.delete(routes.capacityAdminItem(id));
      }
      message.success(`Deleted ${ids.length} project${ids.length > 1 ? "s" : ""}`);
      fetchList();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete selected");
    }
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(routes.capacityAdminList);
      const list =
        data?.projects || data?.careers || data?.programs || data?.rows || [];
      list.sort(
        (a, b) =>
          orderKey(a.order) - orderKey(b.order) ||
          new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setRows(list);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((x) =>
        [x.name, x.programName, x.type, x.referenceNumber]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s))
      );
    }
    if (status !== "all") {
      r = r.filter((x) =>
        status === "published"
          ? x.isPublished === true
          : status === "declined"
          ? x.isPublished === false
          : x.isPublished !== true && x.isPublished !== false
      );
    }
    return r;
  }, [rows, q, status]);

  const setPublish = async (record, publish) => {
    try {
      await axios.patch(routes.capacityAdminPublish(record._id), { publish });
      setRows((prev) =>
        prev.map((r) =>
          r._id === record._id
            ? {
                ...r,
                isPublished: publish === "pending" ? undefined : !!publish,
                publishDate:
                  publish === true ? new Date().toISOString() : undefined,
              }
            : r
        )
      );
      message.success("Status updated");
    } catch (e) {
      message.error("Failed to update publish status");
    }
  };

  const onReorder = async (orderedIds) => {
    try {
      await axios.post(routes.capacityAdminReorder, { orderedIds });
      // optimistic reorder
      setRows((prev) => {
        const byId = new Map(prev.map((r) => [r._id, r]));
        const reordered = orderedIds
          .map((id, i) => ({ ...byId.get(id), order: i + 1 }))
          .filter(Boolean);
        const rest = prev.filter(
          (r) => !byId.has(r._id) || !orderedIds.includes(r._id)
        );
        return [...reordered, ...rest].sort(
          (a, b) =>
            orderKey(a.order) - orderKey(b.order) ||
            new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
      });
      message.success("Reordered");
    } catch (e) {
      message.error("Failed to reorder");
    }
  };

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (record) => {
    setEditing(record);
    setModalOpen(true);
  };

  const onSaved = (updated) => {
    if (!updated) return;
    fetchList();
  };

  const columns = [
    {
      title: "#",
      width: 52,
      align: "center",
      render: (_v, _r, i) => i + 1,
    },
    {
      title: "Logo",
      width: 100,
      render: (_, r) =>
        r.logo ? (
          <Image
            src={r.logo}
            alt=""
            style={{
              width: 80,
              height: 80,
              borderRadius: 6,
              objectFit: "cover",
            }}
          />
        ) : (
          <div
            style={{
              width: 80,
              height: 80,
              background: "#eee",
              borderRadius: 6,
            }}
          />
        ),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (v) => <Text strong>{v || "—"}</Text>,
    },
    {
      title: "Program",
      dataIndex: "programName",
      width: 140,
      render: (_, r) => r.program?.name || "—",
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (v) => v || "—",
    },
    {
      title: "Status",
      width: 130,
      render: (_, r) => (
        <Space>
          <Switch
            checked={r.isPublished === true}
            onChange={(c) => setPublish(r, c ? true : false)}
            checkedChildren="Publish"
            unCheckedChildren="Draft"
          />
          {/* {statusTag(r.isPublished)}{" "} */}
        </Space>
      ),
    },

    {
      title: "Actions",
      fixed: "right",
      render: (_, r) => (
        <Space wrap>
          {/* <Switch
            checked={r.isPublished === true}
            onChange={(c) => setPublish(r, c ? true : false)}
            checkedChildren="Publish"
            unCheckedChildren="Draft"
          /> */}
          <Button icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
          <Button
            icon={<ApartmentOutlined />}
            onClick={() => {
              setPartnersProject(r);
              setPartnersOpen(true);
            }}
          >
            Partners
          </Button>
          <Button
            icon={<NotificationOutlined />}
            onClick={() => {
              setNewsProject(r);
              setNewsOpen(true);
            }}
          >
            News
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: "Delete project",
                content: `You are about to delete ${r.name || "this project"}. This action cannot be undone.`,
                okText: "Delete",
                okButtonProps: { danger: true },
                onOk: () => deleteOne(r),
              });
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <h2 className="adminTitle" style={{ marginBottom: 12 }}>
        Capacity Projects
      </h2>

      {/* <Select
        value={status}
        onChange={setStatus}
        style={{ width: 180 }}
        options={[
          { value: "all", label: "All" },
          { value: "pending", label: "Pending" },
          { value: "published", label: "Published" },
          { value: "declined", label: "Declined" },
        ]}
      /> */}

      <TableWithExport
        columns={columns}
        data={filtered}
        loading={loading}
        tableName="capacity_projects"
        filters={false}
        enableReorder
        getRowId={(r) => r._id}
        onReorder={onReorder}
        addRow={openAdd}
        selectable
        onBulkDelete={(rows) => {
          const many = rows.length > 1;
          Modal.confirm({
            title: many ? `Delete ${rows.length} projects` : "Delete project",
            content: many
              ? `You are about to delete ${rows.length} projects. This action cannot be undone.`
              : `You are about to delete ${rows[0]?.name || "this project"}. This action cannot be undone.`,
            okText: "Delete",
            okButtonProps: { danger: true },
            onOk: async () => deleteMany(rows),
          });
        }}
      />

      <ProjectModal
        open={modalOpen}
        project={editing}
        onClose={() => setModalOpen(false)}
        onSaved={onSaved}
      />

      <Modal
        open={partnersOpen}
        title={`Partners — ${partnersProject?.name || ""}`}
        onCancel={() => setPartnersOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        {partnersProject && (
          <PartnersManager
            projectId={partnersProject._id}
            onChange={() => fetchList()}
          />
        )}
      </Modal>

      <Modal
        open={newsOpen}
        title={`News — ${newsProject?.name || ""}`}
        onCancel={() => setNewsOpen(false)}
        footer={null}
        width={960}
        destroyOnClose
      >
        {newsProject && <NewsAdmin project={newsProject} />}
      </Modal>
    </>
  );
}
